import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ── Button ─────────────────────────────────────────────────────────────────

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
  iconOnly?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  iconOnly = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'btn',
        `btn--${variant}`,
        size === 'sm' && 'btn--sm',
        block && 'btn--block',
        iconOnly && 'btn--icon',
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

export function Spinner({ size = 'sm', label }: { size?: 'sm' | 'lg'; label?: string }) {
  return (
    <span
      className={cx('spinner', size === 'lg' && 'spinner--lg')}
      role={label ? 'status' : undefined}
    >
      {label && <span className="visually-hidden">{label}</span>}
    </span>
  );
}

// ── Form fields ────────────────────────────────────────────────────────────

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  className?: string;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}

function FieldShell({ label, hint, error, className, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('field', className)}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy })}
      {hint && !error && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  fieldClassName?: string;
}

export function TextField({
  label,
  hint,
  error,
  fieldClassName,
  className,
  ...rest
}: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={fieldClassName}>
      {({ id, describedBy }) => (
        <input
          id={id}
          className={cx('input', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  fieldClassName?: string;
  options: { value: string; label: string }[];
}

export function SelectField({
  label,
  hint,
  error,
  fieldClassName,
  options,
  className,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={fieldClassName}>
      {({ id, describedBy }) => (
        <select
          id={id}
          className={cx('select', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  fieldClassName?: string;
}

export function TextAreaField({
  label,
  hint,
  error,
  fieldClassName,
  className,
  ...rest
}: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={fieldClassName}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          className={cx('textarea', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export function Checkbox({
  label,
  description,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  return (
    <label className="checkbox">
      <input type="checkbox" {...rest} />
      <span>
        {label}
        {description && <span className="field__hint"> — {description}</span>}
      </span>
    </label>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────

export function Card({
  title,
  subtitle,
  actions,
  footer,
  flush = false,
  className,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section className={cx('card', className)}>
      {(title || actions) && (
        <header className="card__header">
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {subtitle && <p className="card__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={cx('card__body', flush && 'card__body--flush')}>{children}</div>
      {footer && <footer className="card__footer">{footer}</footer>}
    </section>
  );
}

// ── Badge, Alert, Empty state ──────────────────────────────────────────────

export type Tone = 'neutral' | 'success' | 'warning' | 'critical' | 'info' | 'primary';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx('badge', tone !== 'neutral' && `badge--${tone}`)}>{children}</span>;
}

export function Alert({
  tone = 'info',
  title,
  icon,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'critical';
  title?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={cx('alert', `alert--${tone}`)} role={tone === 'critical' ? 'alert' : 'status'}>
      {icon && <span className="alert__icon">{icon}</span>}
      <div>
        {title && <div className="alert__title">{title}</div>}
        {children}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state__icon">{icon}</div>}
      <div className="empty-state__title">{title}</div>
      {children && <div className="empty-state__body">{children}</div>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

// ── Page header & KPI ──────────────────────────────────────────────────────

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-header__title">{title}</h1>
        {description && <p className="page-header__description">{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </div>
  );
}

export function KpiCard({
  label,
  value,
  icon,
  hint,
}: {
  label: string;
  /** null renders an "unavailable" state rather than a misleading zero. */
  value: ReactNode | null;
  icon?: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="card kpi">
      <div className="kpi__label">
        {icon && <span className="kpi__icon">{icon}</span>}
        {label}
      </div>
      <div className={cx('kpi__value', value === null && 'kpi__value--unavailable')}>
        {value === null ? '—' : value}
      </div>
      {hint && <div className="kpi__hint">{hint}</div>}
    </div>
  );
}

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('');
  return (
    <span className={cx('avatar', size === 'sm' && 'avatar--sm')} aria-hidden="true">
      {initials || '?'}
    </span>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────

export function Modal({
  open,
  title,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelector<HTMLElement>(
      'input, select, textarea, button:not([data-modal-close]), [href]',
    );
    (focusable ?? dialog)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="modal__header">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm"
            onClick={onClose}
            aria-label="Close"
            data-modal-close
          >
            ×
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────

export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` styles the confirm button as destructive. */
  tone?: 'default' | 'danger';
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Themed replacement for `window.confirm`: `if (!(await confirm({ message }))) return;`. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<{
    options: ConfirmOptions;
    resolve: (ok: boolean) => void;
  } | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({
          options: typeof options === 'string' ? { message: options } : options,
          resolve,
        });
      }),
    [],
  );
  const settle = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  const o = pending?.options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={pending !== null}
        title={o?.title ?? 'Are you sure?'}
        onClose={() => settle(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {o?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={o?.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
            >
              {o?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        <p className="confirm__message">{o?.message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}

// ── Toasts ─────────────────────────────────────────────────────────────────

interface ToastItem {
  id: number;
  tone: 'success' | 'critical' | 'info';
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({
  children,
  icons,
}: {
  children: ReactNode;
  icons?: Partial<Record<ToastItem['tone'], ReactNode>>;
}) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastItem['tone'], message: string) => {
      const id = nextId.current++;
      setToasts((list) => [...list.slice(-3), { id, tone, message }]);
      setTimeout(() => dismiss(id), tone === 'critical' ? 8000 : 4500);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('critical', m),
      info: (m) => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx('toast', `toast--${t.tone}`)}
            role={t.tone === 'critical' ? 'alert' : 'status'}
          >
            {icons?.[t.tone] && <span className="toast__icon">{icons[t.tone]}</span>}
            <span className="toast__message">{t.message}</span>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
